import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Sparkles } from "lucide-react";

const journeySteps = [
  { label: "Confusion", hint: "A real question, noisy answers" },
  { label: "Clarity", hint: "Structured guides & mini-sites" },
  { label: "Action", hint: "Verified providers when you're ready" },
];

/**
 * Explainer video block — placeholder until a real embed URL is wired.
 */
const DemoSection = () => {
  const [showModal, setShowModal] = useState(false);

  const videoSurface = (
    <div className="relative aspect-video w-full overflow-hidden rounded-[1.75rem] border border-black/10 bg-gradient-to-br from-gray-900 via-gray-800 to-black shadow-[0_28px_80px_-40px_rgba(0,0,0,0.55)] sm:rounded-[2rem]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(#FFD221 1px, transparent 1px), linear-gradient(90deg, #FFD221 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
        aria-hidden
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-[#FFD221]/10" aria-hidden />

      <div className="relative flex h-full flex-col">
        <div className="pointer-events-none flex flex-1 flex-col items-center justify-center px-6 pb-2 pt-8 text-center sm:pt-10">
          <p className="text-4xl sm:text-5xl" aria-hidden>
            🎥
          </p>
          <p className="mt-3 font-black uppercase tracking-[0.25em] text-[#FFD221] sm:text-sm">
            Video placeholder
          </p>
          <p className="mt-2 text-sm font-bold text-white/90 sm:text-base">
            [Embedded Explainer Video]
          </p>
          <p className="mx-auto mt-2 max-w-sm text-xs leading-relaxed text-white/45">
            Drop in a YouTube, Vimeo, or MP4 embed here when your asset is ready.
          </p>
        </div>
        <div className="flex shrink-0 justify-center pb-8 pt-2">
          <motion.button
            type="button"
            onClick={() => setShowModal(true)}
            className="flex h-[4.25rem] w-[4.25rem] items-center justify-center rounded-full bg-[#FFD221] text-black shadow-[0_16px_50px_-12px_rgba(255,210,33,0.65)] ring-4 ring-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFD221] focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.96 }}
            aria-label="Open explainer video preview"
          >
            <Play size={26} className="ml-1" fill="currentColor" strokeWidth={0} />
          </motion.button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <section className="relative w-full overflow-hidden bg-[#f7f4ec] px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
        <div className="pointer-events-none absolute -left-24 top-1/4 h-80 w-80 rounded-full bg-[#FFD221]/15 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -right-20 bottom-1/4 h-72 w-72 rounded-full bg-[#c9a000]/10 blur-3xl" aria-hidden />

        <div className="relative mx-auto max-w-7xl">
          {/* Header */}
          <motion.div
            className="mx-auto mb-10 max-w-3xl text-center sm:mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#FFD221]/35 bg-white/90 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-[#9a7b00] shadow-sm">
              <Sparkles size={14} strokeWidth={2.5} className="text-[#E5B800]" aria-hidden />
              Explainer
            </span>
            <h2 className="text-[clamp(1.85rem,4vw+0.75rem,3rem)] font-black leading-tight tracking-tight text-gray-950">
              See InfoBoxx in{" "}
              <span className="bg-gradient-to-r from-[#b8860b] via-[#FFD221] to-[#ffec80] bg-clip-text text-transparent">
                Action
              </span>
            </h2>
          </motion.div>

          <div className="grid gap-10 lg:grid-cols-2 lg:items-stretch lg:gap-12 xl:gap-14">
            {/* Left — journey rail (vertically centered vs video height) */}
            <motion.div
              className="order-2 flex h-full min-h-0 flex-col justify-center gap-4 lg:order-1"
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-semibold leading-relaxed text-gray-600">
                One explainer — how we take you from a messy question to a confident next step.
              </p>
              <div className="space-y-3">
                {journeySteps.map((step, i) => (
                  <motion.div
                    key={step.label}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 + i * 0.08 }}
                    className="flex gap-3 rounded-2xl border border-black/[0.07] bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#FFD221] text-xs font-black text-black">
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-sm font-black text-gray-900">{step.label}</p>
                      <p className="text-xs text-gray-500">{step.hint}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              <p className="text-xs font-medium text-gray-400">
                No account needed to preview the story — full product tour coming in your embed.
              </p>
            </motion.div>

            {/* Right — video only (same row height as left for alignment) */}
            <motion.div
              className="order-1 flex min-h-0 lg:order-2"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.1 }}
            >
              <div className="w-full self-center">{videoSurface}</div>
            </motion.div>
          </div>

          <motion.p
            className="mx-auto mt-8 max-w-2xl text-center text-[0.9375rem] font-semibold leading-relaxed text-gray-600 sm:mt-10 sm:text-base"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.05 }}
          >
            Watch how InfoBoxx takes you from confusion to clarity — and from clarity to action.
          </motion.p>
        </div>
      </section>

      {/* Modal — same placeholder, larger */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
            role="presentation"
          >
            <motion.div
              className="relative w-full max-w-4xl overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.28 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                aria-label="Close video"
              >
                <X size={18} />
              </button>
              <div className="relative aspect-video bg-gradient-to-br from-gray-900 to-black">
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                  <span className="text-5xl" aria-hidden>
                    🎥
                  </span>
                  <p className="mt-4 text-xs font-black uppercase tracking-[0.25em] text-[#FFD221]">
                    Embedded Explainer Video
                  </p>
                  <p className="mt-2 max-w-md text-sm text-white/70">
                    Replace this area with your iframe or video player. Hook: paste embed in{" "}
                    <code className="rounded bg-white/10 px-1.5 py-0.5 text-xs text-[#FFD221]">
                      DemoSection
                    </code>{" "}
                    when ready.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DemoSection;
